import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  PolicyRulesJson,
  EmployeeData,
  ExistingMember,
  FamilyMember,
  TopUpSelection,
  PlanDefinition,
  SumInsuredOption,
  TopUpItem,
  FlexiItem,
  RelationType,
} from '../types'

export interface EnrollmentState {
  // Selections
  selectedPlanCode: string | null
  selectedSICode: string | null
  members: FamilyMember[]
  selectedTopUps: TopUpSelection[]
  selectedFlexiCodes: string[]

  // Derived/filtered data
  eligiblePlans: PlanDefinition[]
  eligibleSIOptions: SumInsuredOption[]
  computedSI: number
  eligibleTopUps: TopUpItem[]
  eligibleFlexiItems: FlexiItem[]

  // Actions
  selectPlan: (planCode: string) => void
  selectSI: (siCode: string) => void
  addMember: (relation: RelationType) => void
  updateMember: (id: number, updates: Partial<FamilyMember>) => void
  removeMember: (id: number) => void
  setTopUpSelection: (itemCode: string, tierCode: string | null) => void
  toggleFlexiItem: (itemCode: string) => void
  setFlexiSingle: (itemCode: string | null) => void

  // Helpers
  availableRelationsToAdd: RelationType[]
  isParentBlocked: boolean
  isParentInLawBlocked: boolean
  maxLivesReached: boolean
}

export function useEnrollmentState(
  json: PolicyRulesJson | null,
  employee: EmployeeData | null,
  existingMembers: ExistingMember[] | null
): EnrollmentState {
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null)
  const [selectedSICode, setSelectedSICode] = useState<string | null>(null)
  const [members, setMembers] = useState<FamilyMember[]>([])
  const [selectedTopUps, setSelectedTopUps] = useState<TopUpSelection[]>([])
  const [selectedFlexiCodes, setSelectedFlexiCodes] = useState<string[]>([])
  const [nextMemberId, setNextMemberId] = useState(2)

  // ── Eligible Plans ───────────────────────────────────────
  const eligiblePlans = useMemo(() => {
    if (!json || !employee) return []
    const gradeEntry = json.employee.gradePlanMapping.gradeToPlans.find(
      (g) => g.gradeCode === employee.Grade
    )
    if (!gradeEntry) return []
    return json.plans.plans.filter((p) => gradeEntry.planCodes.includes(p.planCode))
  }, [json, employee])

  // Auto-select first eligible plan (smallest maxLives = minimum dependents)
  useEffect(() => {
    if (eligiblePlans.length >= 1 && !selectedPlanCode) {
      const sorted = [...eligiblePlans].sort((a, b) => a.maxLives - b.maxLives)
      setSelectedPlanCode(sorted[0].planCode)
    }
  }, [eligiblePlans, selectedPlanCode])

  // Auto-switch plan based on member count
  useEffect(() => {
    if (eligiblePlans.length <= 1 || !selectedPlanCode) return
    const memberCount = members.length
    const sorted = [...eligiblePlans].sort((a, b) => a.maxLives - b.maxLives)
    const bestPlan = sorted.find((p) => p.maxLives >= memberCount) || sorted[sorted.length - 1]
    if (bestPlan.planCode !== selectedPlanCode) {
      setSelectedPlanCode(bestPlan.planCode)
      // Preserve SI selection if still valid in the new plan
      if (selectedSICode && json) {
        const newMapping = json.employee.planSumInsuredMapping.planToSumInsured.find(
          (m) => m.planCode === bestPlan.planCode
        )
        if (!newMapping || !newMapping.sumInsuredOptionCodes.includes(selectedSICode)) {
          setSelectedSICode(null)
        }
      }
    }
  }, [members, eligiblePlans, selectedPlanCode, json, selectedSICode])

  // ── Eligible SI Options ──────────────────────────────────
  const eligibleSIOptions = useMemo(() => {
    if (!json || !selectedPlanCode) return []
    const mapping = json.employee.planSumInsuredMapping.planToSumInsured.find(
      (m) => m.planCode === selectedPlanCode
    )
    if (!mapping) return []
    return json.sumInsured.options.filter((o) => mapping.sumInsuredOptionCodes.includes(o.optionCode))
  }, [json, selectedPlanCode])

  // Auto-select single SI
  useEffect(() => {
    if (eligibleSIOptions.length === 1 && !selectedSICode) {
      setSelectedSICode(eligibleSIOptions[0].optionCode)
    } else if (
      selectedSICode &&
      !eligibleSIOptions.find((o) => o.optionCode === selectedSICode)
    ) {
      setSelectedSICode(eligibleSIOptions.length === 1 ? eligibleSIOptions[0].optionCode : null)
    }
  }, [eligibleSIOptions, selectedSICode])

  // ── Computed SI Value ────────────────────────────────────
  const computedSI = useMemo(() => {
    if (!selectedSICode) return 0
    const option = json?.sumInsured.options.find((o) => o.optionCode === selectedSICode)
    if (!option) return 0
    if (option.type === 'Fixed') return option.fixedAmount ?? 0
    if (option.type === 'CtcMultiplier' && option.multiplier && employee?.CTC) {
      let val = employee.CTC * option.multiplier
      if (option.minCap != null) val = Math.max(val, option.minCap)
      if (option.maxCap != null) val = Math.min(val, option.maxCap)
      return Math.round(val)
    }
    return 0
  }, [json, selectedSICode, employee])

  // ── Initialize Self member ───────────────────────────────
  useEffect(() => {
    if (!employee) return
    // Only initialize once — skip if members already populated
    setMembers((prev) => {
      if (prev.length > 0) return prev
      if (existingMembers && existingMembers.length > 0) {
        const restored: FamilyMember[] = existingMembers.map((m, idx) => ({
          id: idx + 1,
          name: m.Name ?? '',
          dob: m.Dob?.split('T')[0] ?? '',
          relation: m.Relation as RelationType,
          gender: m.Gender ?? '',
          premiumAmount: m.Premium || 0,
        }))
        setNextMemberId(restored.length + 1)
        return restored
      }
      return [
        {
          id: 1,
          name: employee.EmployeeName ?? '',
          dob: employee.DOB?.split('T')[0] ?? '',
          relation: 'Self',
          gender: employee.Gender ?? '',
          premiumAmount: 0,
        },
      ]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, existingMembers])

  // ── Selected Plan (for premium calculation — smallest fitting plan) ──
  const selectedPlan = useMemo(() => {
    return eligiblePlans.find((p) => p.planCode === selectedPlanCode) ?? null
  }, [eligiblePlans, selectedPlanCode])

  // ── Form Plan (for UI constraints — largest plan, max member capacity) ──
  const formPlan = useMemo(() => {
    if (eligiblePlans.length === 0) return null
    if (eligiblePlans.length === 1) return eligiblePlans[0]
    return [...eligiblePlans].sort((a, b) => b.maxLives - a.maxLives)[0]
  }, [eligiblePlans])

  // ── Parent combination logic ─────────────────────────────
  const hasParent = members.some((m) => m.relation === 'Parent')
  const hasParentInLaw = members.some((m) => m.relation === 'ParentInLaw')
  const isEitherMode = (formPlan?.parentCombinationMode ?? selectedPlan?.parentCombinationMode) === 'EitherParentsOrInLaws'

  const isParentBlocked = isEitherMode && hasParentInLaw
  const isParentInLawBlocked = isEitherMode && hasParent

  // ── Available Relations to Add (uses formPlan for max capacity) ──
  const availableRelationsToAdd = useMemo((): RelationType[] => {
    if (!formPlan) return []
    const allowed = formPlan.allowedRelations.filter((r) => r !== 'Self')
    return allowed.filter((relation) => {
      // Check parentCombinationMode
      if (relation === 'Parent' && isParentBlocked) return false
      if (relation === 'ParentInLaw' && isParentInLawBlocked) return false

      // Check maxCount from the largest plan
      const rule = formPlan.planRelationRules.find((r) => r.relation === relation)
      if (rule) {
        const currentCount = members.filter((m) => m.relation === relation).length
        if (currentCount >= rule.maxCount) return false
      }
      return true
    })
  }, [formPlan, members, isParentBlocked, isParentInLawBlocked])

  const maxLivesReached = formPlan ? members.length >= formPlan.maxLives : false

  // ── Eligible Top-Ups ─────────────────────────────────────
  const eligibleTopUps = useMemo(() => {
    if (!json?.topUps?.enabled || !employee || !selectedPlanCode) return []
    return json.topUps.items.filter((item) => {
      if (!item.allowedGradeCodes.includes(employee.Grade)) return false
      if (item.allowedPlanCodes && !item.allowedPlanCodes.includes(selectedPlanCode)) return false
      return true
    })
  }, [json, employee, selectedPlanCode])

  // ── Eligible Flexi Items ─────────────────────────────────
  const eligibleFlexiItems = useMemo(() => {
    if (!json?.flexi?.enabled || !employee || !selectedPlanCode) return []
    return json.flexi.items.filter((item) => {
      if (!item.allowedGradeCodes.includes(employee.Grade)) return false
      if (item.allowedPlanCodes && !item.allowedPlanCodes.includes(selectedPlanCode)) return false
      return true
    })
  }, [json, employee, selectedPlanCode])

  // ── Actions ──────────────────────────────────────────────
  const selectPlan = useCallback(
    (planCode: string) => {
      setSelectedPlanCode(planCode)
      setSelectedSICode(null)
      setSelectedTopUps([])
      setSelectedFlexiCodes([])
      // Remove members that don't fit new plan (keep Self)
      const newPlan = json?.plans.plans.find((p) => p.planCode === planCode)
      if (newPlan) {
        setMembers((prev) =>
          prev.filter(
            (m) => m.relation === 'Self' || newPlan.allowedRelations.includes(m.relation)
          )
        )
      }
    },
    [json]
  )

  const selectSI = useCallback((siCode: string) => {
    setSelectedSICode(siCode)
  }, [])

  const addMember = useCallback(
    (relation: RelationType) => {
      if (maxLivesReached) return
      setMembers((prev) => [
        ...prev,
        { id: nextMemberId, name: '', dob: '', relation, gender: '', premiumAmount: 0 },
      ])
      setNextMemberId((n) => n + 1)
    },
    [nextMemberId, maxLivesReached]
  )

  const updateMember = useCallback((id: number, updates: Partial<FamilyMember>) => {
    setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, ...updates } : m)))
  }, [])

  const removeMember = useCallback((id: number) => {
    setMembers((prev) => prev.filter((m) => m.id !== id || m.relation === 'Self'))
  }, [])

  const setTopUpSelection = useCallback(
    (itemCode: string, tierCode: string | null) => {
      if (!json?.topUps) return
      if (!json.topUps.allowMultiple) {
        // Single mode: replace all
        if (tierCode) {
          setSelectedTopUps([{ itemCode, selectedTierCode: tierCode }])
        } else {
          setSelectedTopUps([])
        }
      } else {
        // Multi mode: toggle
        setSelectedTopUps((prev) => {
          if (!tierCode) return prev.filter((s) => s.itemCode !== itemCode)
          const idx = prev.findIndex((s) => s.itemCode === itemCode)
          if (idx >= 0) {
            const next = [...prev]
            next[idx] = { itemCode, selectedTierCode: tierCode }
            return next
          }
          return [...prev, { itemCode, selectedTierCode: tierCode }]
        })
      }
    },
    [json]
  )

  const toggleFlexiItem = useCallback(
    (itemCode: string) => {
      if (!json?.flexi) return
      setSelectedFlexiCodes((prev) => {
        if (prev.includes(itemCode)) {
          return prev.filter((c) => c !== itemCode)
        }
        // Handle excludesItems
        const item = json.flexi.items.find((i) => i.itemCode === itemCode)
        let next = [...prev, itemCode]
        if (item?.excludesItems) {
          next = next.filter((c) => !item.excludesItems!.includes(c))
        }
        return next
      })
    },
    [json]
  )

  const setFlexiSingle = useCallback((itemCode: string | null) => {
    setSelectedFlexiCodes(itemCode ? [itemCode] : [])
  }, [])

  return {
    selectedPlanCode,
    selectedSICode,
    members,
    selectedTopUps,
    selectedFlexiCodes,
    eligiblePlans,
    eligibleSIOptions,
    computedSI,
    eligibleTopUps,
    eligibleFlexiItems,
    selectPlan,
    selectSI,
    addMember,
    updateMember,
    removeMember,
    setTopUpSelection,
    toggleFlexiItem,
    setFlexiSingle,
    availableRelationsToAdd,
    isParentBlocked,
    isParentInLawBlocked,
    maxLivesReached,
  }
}
